//
/* GS://PROTOCOLO
 * Arquivo: src/services/gs_nf_module_service.js
 * Dossiê de Projeto e Colaboração: Ecossistema GoldenSisters
 * Versão: 3.8.0 – GUARDRAIL: Categoria Obrigatória
 * Impacto da Versão: [src/services/gs_nf_module_service.js]
 * Data: 26/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [SEGURANÇA]: Implementada validação impeditiva na normalização de itens.
 * - REGRA: Se o item for BRUTA ou PRONTA e não tiver Categoria, lança erro.
 * - OBJETIVO: Impedir a geração de SKUs "GEN" (Genéricos) e garantir rastreabilidade.
 */
//

import { db } from '../firebase/config';
import {
  collection,
  doc,
  runTransaction,
  serverTimestamp,
  Timestamp,
  getDocs,
  writeBatch,
} from 'firebase/firestore';

import {
  GSM_COLLECTIONS,
  ITEM_TYPES,
  INVENTORY_MOVEMENT_TYPES,
  FINANCIAL_INPUT_TYPES,
} from './gs_nf_module_dataModel';

// [GS-ADICIONADO] Ponte com o motor de produtos
import productService from './productService';

// [GS-ADICIONADO] Constantes Financeiras
import { FINANCIAL_TYPES, FINANCIAL_STATUS } from '../utils/constants';

/**
 * Normaliza a data da NF (string "YYYY-MM-DD" ou similar) para Timestamp.
 */
const normalizeNFDate = (nfDate) => {
  if (!nfDate) {
    return serverTimestamp();
  }
  try {
    const d = new Date(nfDate);
    if (Number.isNaN(d.getTime())) {
      return serverTimestamp();
    }
    return Timestamp.fromDate(d);
  } catch {
    return serverTimestamp();
  }
};

/**
 * Normaliza o cabeçalho da NF a partir da entrada da tela.
 */
const normalizeNFHeader = (nfHeaderInput = {}, userContext = {}) => {
  const {
    nfNumber = '',
    supplierName = '',
    supplierId = '',
    nfDate = '',
    notes = '',
  } = nfHeaderInput;

  const createdByUserId = userContext.userId || userContext.uid || '';

  return {
    nfNumber: String(nfNumber || '').trim(),
    supplierId: supplierId ? String(supplierId) : '',
    supplierName: String(supplierName || '').trim(),
    nfDate: normalizeNFDate(nfDate),
    createdAt: serverTimestamp(),
    createdByUserId,

    grossTotal: 0,
    servicesTotal: 0,
    expensesTotal: 0,
    grandTotal: 0,
    currency: 'BRL',
    expensesBreakdown: [],
    status: 'REGISTRADA',
    notes: String(notes || '').trim(),
  };
};

/**
 * Normaliza itens brutos vindos da tela.
 */
const normalizeNFItems = (itemsInput = [], userContext = {}) => {
  const createdByUserId = userContext.userId || userContext.uid || '';
  let lineNumber = 1;

  const result = [];

  for (const rawItem of itemsInput) {
    const itemTypeRaw = (rawItem.itemType || '').toUpperCase();
    const itemType =
      itemTypeRaw === ITEM_TYPES.BRUTA ||
      itemTypeRaw === ITEM_TYPES.PRONTA ||
      itemTypeRaw === ITEM_TYPES.SERVICO
        ? itemTypeRaw
        : ITEM_TYPES.BRUTA;

    const quantity = Number(rawItem.quantity ?? 0) || 0;
    const unitValue = Number(rawItem.unitValue ?? 0) || 0;
    const rawWeight = Number(rawItem.rawWeight ?? 0) || 0;

    const grossCost = quantity * unitValue;

    const unit = rawItem.unit || 'un';

    const supplierItemCode = String(rawItem.supplierItemCode || '').trim();
    const supplierItemName = String(rawItem.supplierItemName || '').trim();

    const categoryId = rawItem.categoryId || null;

    // [GS-GUARDRAIL]: Validação Obrigatória de Categoria para evitar SKU "GEN"
    if ((itemType === ITEM_TYPES.BRUTA || itemType === ITEM_TYPES.PRONTA) && !categoryId) {
      throw new Error(`A categoria é obrigatória para o item da linha ${lineNumber} (${supplierItemName || 'Sem Nome'}).`);
    }

    let tags = [];
    if (Array.isArray(rawItem.tags)) {
      tags = rawItem.tags.map((t) => String(t || '').trim()).filter(Boolean);
    } else if (typeof rawItem.tags === 'string') {
      tags = rawItem.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
    }

    result.push({
      nfId: null,
      lotId: null,
      lineNumber: lineNumber++,
      itemType,
      supplierId: rawItem.supplierId || '',
      supplierItemCode,
      supplierItemName,
      categoryId,
      tags,
      quantity,
      unit,
      rawWeight,
      unitValue,
      grossCost,
      rateioCost: 0,
      finalCost: grossCost,
      createdAt: serverTimestamp(),
      createdByUserId,
      // [GS-NOVO] Atributos para precificação inteligente vindo da tela
      decidedPrice: Number(rawItem.decidedPrice) || 0,
      suggestedPrice: Number(rawItem.suggestedPrice) || 0,
    });
  }

  return result;
};

/**
 * Calcula o total de despesas extras (frete, pedágio, etc.).
 */
const calculateExpensesTotals = (expensesInput = {}) => {
  const {
    frete = 0,
    pedagio = 0,
    combustivel = 0,
    alimentacao = 0,
    outros = 0,
  } = expensesInput;

  const freteNum = Number(frete) || 0;
  const pedagioNum = Number(pedagio) || 0;
  const combustivelNum = Number(combustivel) || 0;
  const alimentacaoNum = Number(alimentacao) || 0;
  const outrosNum = Number(outros) || 0;

  const totalExpenses =
    freteNum + pedagioNum + combustivelNum + alimentacaoNum + outrosNum;

  return {
    frete: freteNum,
    pedagio: pedagioNum,
    combustivel: combustivelNum,
    alimentacao: alimentacaoNum,
    outros: outrosNum,
    totalExpenses,
  };
};

/**
 * Distribui as despesas extras entre os itens BRUTA/PRONTA.
 * Serviços não participam.
 */
const distributeExpensesByItem = (items = [], totalExpenses = 0) => {
  if (!totalExpenses || totalExpenses <= 0) {
    return items.map((it) => ({
      ...it,
      rateioCost: 0,
      finalCost: it.grossCost,
    }));
  }

  const eligibleItems = items.filter(
    (it) => it.itemType === ITEM_TYPES.BRUTA || it.itemType === ITEM_TYPES.PRONTA
  );

  const totalEligibleGross = eligibleItems.reduce(
    (sum, it) => sum + (Number(it.grossCost) || 0),
    0
  );

  if (!totalEligibleGross || totalEligibleGross <= 0) {
    return items.map((it) => ({
      ...it,
      rateioCost: 0,
      finalCost: it.grossCost,
    }));
  }

  const updated = items.map((it) => {
    if (it.itemType !== ITEM_TYPES.BRUTA && it.itemType !== ITEM_TYPES.PRONTA) {
      return {
        ...it,
        rateioCost: 0,
        finalCost: it.grossCost,
      };
    }

    const portion = (Number(it.grossCost) || 0) / totalEligibleGross || 0;
    const rateioCost = portion * totalExpenses;
    const finalCost = (Number(it.grossCost) || 0) + rateioCost;

    return {
      ...it,
      rateioCost,
      finalCost,
    };
  });

  return updated;
};

/** Gera L2025-XXXXXX */
const generateLotNumber = () => {
  const now = new Date();
  const year = now.getFullYear();
  const base36 = now.getTime().toString(36).toUpperCase();
  return `L${year}-${base36}`;
};

/**
 * Função principal: cria NF, lote, itens, despesas, movimentos e Produtos.
 */
export const createNFEntryWithItemsAndLot = async (
  nfHeaderInput,
  itemsInput,
  expensesInput = {},
  userContext = {}
) => {
  if (!Array.isArray(itemsInput) || itemsInput.length === 0) {
    throw new Error('A NF precisa ter ao menos um item.');
  }

  const nfHeaderNormalized = normalizeNFHeader(nfHeaderInput, userContext);
  // [GS-GUARDRAIL] Validação de categoria ocorre aqui dentro
  const itemsNormalized = normalizeNFItems(itemsInput, userContext);
  const expensesNormalized = calculateExpensesTotals(expensesInput);

  const { totalExpenses } = expensesNormalized;

  const itemsWithRateio = distributeExpensesByItem(itemsNormalized, totalExpenses);

  const grossTotal = itemsWithRateio.reduce(
    (sum, it) => sum + (Number(it.grossCost) || 0),
    0
  );

  const servicesTotal = itemsWithRateio
    .filter((it) => it.itemType === ITEM_TYPES.SERVICO)
    .reduce((sum, it) => sum + (Number(it.grossCost) || 0), 0);

  const grandTotal = grossTotal + totalExpenses;

  const lotNumber = generateLotNumber();
  const createdByUserId = nfHeaderNormalized.createdByUserId;

  const result = await runTransaction(db, async (transaction) => {
    // --- 1) NF ---
    const nfDocRef = doc(collection(db, GSM_COLLECTIONS.NF_ENTRIES));
    const totalItems = itemsWithRateio.length;

    // [GS-FIX] Sanitiza array de itens para remover serverTimestamp() que falha dentro de arrays
    const itemsForArray = itemsWithRateio.map(item => ({
        ...item,
        createdAt: new Date() // Substitui sentinela por data estática
    }));

    const nfData = {
      ...nfHeaderNormalized,
      grossTotal,
      servicesTotal,
      expensesTotal: totalExpenses,
      grandTotal,
      lotId: null,
      totalItems,
      itemsCount: totalItems,
      // [GS-FIX] Usa o array sanitizado
      items: itemsForArray, 
    };
    transaction.set(nfDocRef, nfData);
    const nfId = nfDocRef.id;

    // --- 2) Lote ---
    const lotDocRef = doc(collection(db, GSM_COLLECTIONS.LOTS));
    const totalRawItems = itemsWithRateio.filter((it) => it.itemType === ITEM_TYPES.BRUTA).length;
    const totalFinishedItems = itemsWithRateio.filter((it) => it.itemType === ITEM_TYPES.PRONTA).length;
    const totalServiceItems = itemsWithRateio.filter((it) => it.itemType === ITEM_TYPES.SERVICO).length;

    const lotData = {
      nfId,
      nfNumber: nfHeaderNormalized.nfNumber || '',
      nfDate: nfHeaderNormalized.nfDate || serverTimestamp(),
      supplierId: nfHeaderNormalized.supplierId || '',
      supplierName: nfHeaderNormalized.supplierName || '',
      lotNumber,
      createdAt: serverTimestamp(),
      createdByUserId,
      statusLot: 'ABERTO',
      status: 'ABERTO',
      totalRawItems,
      totalFinishedItems,
      totalServiceItems,
      counts: {
        totalItensNF: itemsWithRateio.length,
        qtdBrutas: totalRawItems,
        qtdProntas: totalFinishedItems,
        qtdServicos: totalServiceItems,
      },
      totals: {
        subtotalItens: grossTotal,
        totalRateio: totalExpenses,
        totalGeral: grandTotal,
      },
      bathOperationsIds: [],
      assemblyIds: [],
      notes: '',
    };

    transaction.set(lotDocRef, lotData);
    const lotId = lotDocRef.id;
    transaction.update(nfDocRef, { lotId });

    // --- 3) Itens da NF + Itens do Lote + [GS-NOVO] Injeção de Produtos ---
    const nfItemsIds = [];
    const lotItemsCollectionRef = collection(db, GSM_COLLECTIONS.LOTS, lotId, 'items');

    for (const item of itemsWithRateio) {
      // 3.1 – nf_items (Documentos individuais aceitam serverTimestamp)
      const nfItemRef = doc(collection(db, GSM_COLLECTIONS.NF_ITEMS));
      const nfItemData = { ...item, nfId, lotId };
      transaction.set(nfItemRef, nfItemData);
      nfItemsIds.push({ id: nfItemRef.id, ...nfItemData });

      // 3.2 – lot_items
      const quantity = Number(item.quantity) || 0;
      const qtyForDivision = quantity > 0 ? quantity : 1;
      const rateioTotal = Number(item.rateioCost) || 0;
      const finalCostTotal = Number(item.finalCost) || 0;
      const rateioPerItem = rateioTotal / qtyForDivision;
      const costBrutoUnitario = finalCostTotal / qtyForDivision;

      const lotItemRef = doc(lotItemsCollectionRef);
      const lotItemData = {
        nfItemIndex: item.lineNumber,
        itemType: item.itemType,
        statusPiece: item.itemType,
        descriptionNF: item.supplierItemName || '',
        supplierProductCode: item.supplierItemCode || '',
        categoryId: item.categoryId || null,
        quantity,
        weightGrams: Number(item.rawWeight) || 0,
        unitPriceNF: Number(item.unitValue) || 0,
        totalItemNF: Number(item.grossCost) || 0,
        rateioPerItem,
        costTotalInicial: costBrutoUnitario,
        costBruto: costBrutoUnitario,
        createdAt: serverTimestamp(),
      };
      transaction.set(lotItemRef, lotItemData);

      // [GS-NOVO] 3.3 – Criação Automática do Produto (Fim do Limbo)
      if (item.itemType === ITEM_TYPES.PRONTA || item.itemType === ITEM_TYPES.BRUTA) {
        const productRef = doc(collection(db, 'products'));
        const generatedSku = await productService.generateSmartSku(
          item.categoryId,
          nfHeaderNormalized.supplierId
        );
        const isReady = item.itemType === ITEM_TYPES.PRONTA;

        const productPayload = {
          name: item.supplierItemName,
          supplierProductName: item.supplierItemName,
          sku: generatedSku,
          categoryId: item.categoryId,
          supplierItemCode: item.supplierItemCode,
          costType: item.itemType,
          cost: costBrutoUnitario,
          costs: {
            purchaseCost: Number(item.unitValue) || 0,
            logisticsAllocatedCost: rateioPerItem,
          },
          price: Number(item.decidedPrice) || 0,
          suggestedPrice: Number(item.suggestedPrice) || 0,
          stock: quantity,
          status: isReady ? 'Aguardando Precificação' : 'Almoxarifado',
          location: isReady ? 'Em Loja (Aguardando)' : 'Almoxarifado',
          pdvEnabled: false,
          isActive: true,
          nfId,
          nfNumber: nfHeaderNormalized.nfNumber,
          lotId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          dimensions: {
            height: 0,
            length: 0,
            width: 0,
            weight: Number(item.rawWeight) || 0,
          },
        };
        transaction.set(productRef, productPayload);
      }
    }

    // --- 4) despesas financeiras (Rateio Analítico) ---
    const expensesToPersist = [];

    if (expensesNormalized.frete > 0) expensesToPersist.push({ type: FINANCIAL_INPUT_TYPES.FRETE, label: 'Frete', amount: expensesNormalized.frete });
    if (expensesNormalized.pedagio > 0) expensesToPersist.push({ type: FINANCIAL_INPUT_TYPES.PEDAGIO, label: 'Pedágio', amount: expensesNormalized.pedagio });
    if (expensesNormalized.combustivel > 0) expensesToPersist.push({ type: FINANCIAL_INPUT_TYPES.COMBUSTIVEL, label: 'Combustível', amount: expensesNormalized.combustivel });
    if (expensesNormalized.alimentacao > 0) expensesToPersist.push({ type: FINANCIAL_INPUT_TYPES.ALIMENTACAO, label: 'Alimentação', amount: expensesNormalized.alimentacao });
    if (expensesNormalized.outros > 0) expensesToPersist.push({ type: FINANCIAL_INPUT_TYPES.OUTROS, label: 'Outros', amount: expensesNormalized.outros });

    for (const exp of expensesToPersist) {
      const dist = [];
      const eligible = nfItemsIds.filter((it) => it.itemType === ITEM_TYPES.BRUTA || it.itemType === ITEM_TYPES.PRONTA);
      const totalEligibleGross = eligible.reduce((sum, it) => sum + (Number(it.grossCost) || 0), 0);
      let distributed = false;

      if (totalEligibleGross > 0) {
        distributed = true;
        for (const it of eligible) {
          const portion = (Number(it.grossCost) || 0) / totalEligibleGross || 0;
          const value = portion * exp.amount;
          if (value > 0) {
            dist.push({ nfItemId: it.id, amount: value });
          }
        }
      }

      const finRef = doc(collection(db, GSM_COLLECTIONS.FINANCIAL_INPUTS));
      const finData = { nfId, lotId, type: exp.type, description: `${exp.label} NF ${nfHeaderNormalized.nfNumber || nfId}`, totalAmount: exp.amount, currency: 'BRL', distributed, distribution: dist, createdAt: serverTimestamp(), createdByUserId };
      transaction.set(finRef, finData);
    }

    // --- 5) movimentos de estoque ---
    for (const it of nfItemsIds) {
      if (it.itemType !== ITEM_TYPES.BRUTA && it.itemType !== ITEM_TYPES.PRONTA) continue;
      const invRef = doc(collection(db, GSM_COLLECTIONS.INVENTORY_MOVEMENTS));
      const qty = Number(it.quantity) || 0;
      const invData = { productId: null, sku: '', movementType: INVENTORY_MOVEMENT_TYPES.NF_ENTRADA, quantityBefore: 0, quantityAfter: qty, quantityDelta: qty, nfId, nfItemId: it.id, lotId, bathOperationId: null, assemblyId: null, statusAtEntry: it.itemType, saleId: null, createdAt: serverTimestamp(), createdByUserId, notes: 'Entrada de estoque via NF (módulo novo).' };
      transaction.set(invRef, invData);
    }

    // --- 6) [GS-NEW] INTEGRAÇÃO FINANCEIRA AUTOMÁTICA (CONTA A PAGAR) ---
    // Criação do registro no Ledger Unificado
    if (grandTotal > 0) {
        const ledgerRef = doc(collection(db, 'financial_ledger'));
        
        // [GS-FIX] Determina status baseado na condição de pagamento (Vista = Pago, Prazo = Pendente)
        const isPaid = nfHeaderInput.paymentCondition === 'vista';
        const ledgerStatus = isPaid ? FINANCIAL_STATUS.PAID : FINANCIAL_STATUS.PENDING;
        const paymentMethod = nfHeaderInput.paymentMethod || 'Outros';

        // Cálculo de Vencimento
        const issueDate = nfHeaderInput.nfDate ? new Date(nfHeaderInput.nfDate) : new Date();
        const dueDate = new Date(issueDate);
        dueDate.setDate(dueDate.getDate() + 30); // Regra padrão 30 dias se prazo

        const ledgerData = {
            type: FINANCIAL_TYPES.EXPENSE,
            category: { id: 'purchase_nf', name: 'Compras (NF)', path: 'Despesas > Compras' },
            description: `Compra NF #${nfHeaderNormalized.nfNumber} - ${nfHeaderNormalized.supplierName}`,
            amountGross: grandTotal,
            amountNet: grandTotal, // Assumindo valor cheio por enquanto
            status: ledgerStatus,
            paymentBreakdown: {
                method: paymentMethod,
                amountPaid: isPaid ? grandTotal : 0
            },
            dates: {
                issue: nfHeaderNormalized.nfDate,
                due: Timestamp.fromDate(dueDate),
                payment: isPaid ? Timestamp.now() : null // Se for à vista, data de pagamento é agora
            },
            origin: {
                module: 'NF_ENTRY',
                referenceId: nfId,
                supplierId: nfHeaderNormalized.supplierId
            },
            createdAt: serverTimestamp()
        };
        transaction.set(ledgerRef, ledgerData);
    }

    return {
      nfId,
      lotId,
      lotNumber,
      grossTotal,
      servicesTotal,
      expensesTotal: totalExpenses,
      grandTotal,
      totalItems,
      totals: {
        rawItems: totalRawItems,
        finishedItems: totalFinishedItems,
        serviceItems: totalServiceItems,
      },
    };
  });

  return result;
};

/**
 * Cria uma nova Ordem de Montagem na coleção "assemblies".
 * Não realiza nenhuma movimentação de estoque nesta fase.
 */
export const createAssemblyOrder = async (orderInput = {}, userContext = {}) => {
  const createdByUserId = userContext.userId || userContext.uid || '';

  const orderReference = String(orderInput.orderReference || '').trim();
  const finalSku = String(orderInput.finalSku || '').trim();
  const orderDescription = String(orderInput.orderDescription || '').trim();
  const assemblyTypeRaw = (orderInput.assemblyType || 'SIMPLES').toUpperCase();
  const assemblyType = assemblyTypeRaw === 'COMPLEXA' ? 'COMPLEXA' : 'SIMPLES';

  const qty = Number(orderInput.orderQuantity) || 0;
  if (!orderReference && !finalSku) {
    throw new Error(
      'Informe ao menos a referência da montagem ou o SKU final para criar a ordem.'
    );
  }
  if (qty <= 0) {
    throw new Error(
      'A quantidade de conjuntos da Ordem de Montagem deve ser maior que zero.'
    );
  }

  const statusRaw = (orderInput.status || 'ABERTA').toUpperCase();
  const allowedStatus = ['ABERTA', 'EM_EXECUCAO', 'CONCLUIDA', 'CANCELADA'];
  const status = allowedStatus.includes(statusRaw) ? statusRaw : 'ABERTA';

  const lotId = orderInput.lotId ? String(orderInput.lotId).trim() : null;
  const nfId = orderInput.nfId ? String(orderInput.nfId).trim() : null;

  const result = await runTransaction(db, async (transaction) => {
    const assemblyRef = doc(collection(db, GSM_COLLECTIONS.ASSEMBLIES));
    const now = serverTimestamp();

    const data = {
      status,
      orderReference,
      finalSku,
      orderDescription,
      orderQuantity: qty,
      assemblyType,
      lotId,
      nfId,
      createdAt: now,
      createdByUserId,
      updatedAt: now,
      updatedByUserId: createdByUserId,
    };

    transaction.set(assemblyRef, data);

    return {
      id: assemblyRef.id,
      ...data,
    };
  });

  return result;
};

/**
 * Atualiza o status de uma Ordem de Montagem existente na coleção "assemblies".
 * Também registra updatedAt e updatedByUserId.
 */
export const updateAssemblyOrderStatus = async (
  assemblyId,
  newStatus,
  userContext = {}
) => {
  if (!assemblyId || !newStatus) {
    throw new Error('assemblyId e newStatus são obrigatórios.');
  }

  const allowedStatus = ['ABERTA', 'EM_EXECUCAO', 'CONCLUIDA', 'CANCELADA'];
  const normalizedStatus = String(newStatus || '').toUpperCase();

  if (!allowedStatus.includes(normalizedStatus)) {
    throw new Error(`Status de montagem inválido: ${newStatus}`);
  }

  const updatedByUserId = userContext.userId || userContext.uid || '';

  await runTransaction(db, async (transaction) => {
    const assemblyRef = doc(collection(db, GSM_COLLECTIONS.ASSEMBLIES), assemblyId);

    transaction.update(assemblyRef, {
      status: normalizedStatus,
      updatedAt: serverTimestamp(),
      updatedByUserId,
    });
  });
};

/**
 * Registra componentes vinculados a uma Ordem de Montagem
 * na subcoleção assemblies/{orderId}/components.
 */
export const addAssemblyComponents = async (
  orderId,
  componentsInput = [],
  userContext = {}
) => {
  if (!orderId) {
    throw new Error('orderId é obrigatório para registrar componentes da montagem.');
  }

  if (!Array.isArray(componentsInput) || componentsInput.length === 0) {
    throw new Error('Informe ao menos um componente na lista componentsInput.');
  }

  const createdByUserId = userContext.userId || userContext.uid || '';

  const result = await runTransaction(db, async (transaction) => {
    const assemblyRef = doc(collection(db, GSM_COLLECTIONS.ASSEMBLIES), orderId);

    const assemblySnap = await transaction.get(assemblyRef);
    if (!assemblySnap.exists()) {
      throw new Error('Ordem de Montagem não encontrada para registrar componentes.');
    }

    const assemblyData = assemblySnap.data() || {};
    const orderQuantity = Number(assemblyData.orderQuantity) || 1;

    const componentsColRef = collection(
      db,
      GSM_COLLECTIONS.ASSEMBLIES,
      orderId,
      'components'
    );

    const now = serverTimestamp();
    const persistedComponents = [];

    for (const raw of componentsInput) {
      const qtyPerSet = Number(raw.qtyPerSet) || 0;
      if (qtyPerSet <= 0) {
        throw new Error(
          'Cada componente deve ter qtyPerSet maior que zero (quantidade por conjunto).'
        );
      }

      const totalQty = qtyPerSet * orderQuantity;

      const componentRef = doc(componentsColRef);
      const data = {
        description: String(raw.description || '').trim(),
        componentRole: String(raw.componentRole || '').trim(),
        qtyPerSet,
        totalQty,
        lotId: raw.lotId ? String(raw.lotId).trim() : null,
        lotItemId: raw.lotItemId ? String(raw.lotItemId).trim() : null,
        notes: String(raw.notes || '').trim(),
        createdAt: now,
        createdByUserId,
      };

      if (typeof raw.snapshotCostPerUnit === 'number') {
        data.snapshotCostPerUnit = raw.snapshotCostPerUnit;
      }
      if (typeof raw.snapshotTotalCost === 'number') {
        data.snapshotTotalCost = raw.snapshotTotalCost;
      }

      transaction.set(componentRef, data);
      persistedComponents.push({ id: componentRef.id, ...data });
    }

    return {
      orderId,
      orderQuantity,
      components: persistedComponents,
    };
  });

  return result;
};

/**
 * Remove todos os componentes de uma Ordem de Montagem
 * (subcoleção assemblies/{orderId}/components).
 */
export const clearAssemblyComponents = async (orderId) => {
  if (!orderId) {
    throw new Error('orderId é obrigatório para limpar componentes da montagem.');
  }

  const componentsColRef = collection(db, GSM_COLLECTIONS.ASSEMBLIES, orderId, 'components');

  const snapshot = await getDocs(componentsColRef);
  if (snapshot.empty) {
    return { orderId, deletedCount: 0 };
  }

  const batch = writeBatch(db);
  snapshot.docs.forEach((docSnap) => {
    batch.delete(docSnap.ref);
  });

  await batch.commit();

  return { orderId, deletedCount: snapshot.size };
};

/**
 * finalizeAssemblyOrder
 */
export const finalizeAssemblyOrder = async (assemblyId, userContext = {}) => {
  if (!assemblyId) {
    throw new Error('assemblyId é obrigatório para finalizar a Ordem de Montagem.');
  }

  const componentsColRef = collection(db, GSM_COLLECTIONS.ASSEMBLIES, assemblyId, 'components');

  const compSnap = await getDocs(componentsColRef);

  if (compSnap.empty) {
    throw new Error('Não é possível concluir a Ordem de Montagem sem componentes cadastrados.');
  }

  let totalComponentsQty = 0;
  let totalComponentsCost = 0;

  compSnap.forEach((docSnap) => {
    const data = docSnap.data() || {};
    const quantity = Number(data.quantity ?? data.totalQty ?? 0) || 0;

    const unitCost = Number(data.unitCost ?? data.snapshotCostPerUnit ?? 0);

    let totalCost = Number(data.totalCost ?? data.snapshotTotalCost ?? 0);

    if (!totalCost && quantity > 0 && unitCost > 0) {
      totalCost = quantity * unitCost;
    }

    totalComponentsQty += quantity;
    totalComponentsCost += totalCost;
  });

  const updatedByUserId = userContext.userId || userContext.uid || '';

  await runTransaction(db, async (transaction) => {
    const assemblyRef = doc(collection(db, GSM_COLLECTIONS.ASSEMBLIES), assemblyId);
    const assemblySnap = await transaction.get(assemblyRef);

    if (!assemblySnap.exists()) {
      throw new Error('Ordem de Montagem não encontrada para finalização.');
    }

    const assemblyData = assemblySnap.data() || {};
    const statusRaw = String(assemblyData.status || 'ABERTA').toUpperCase();

    if (statusRaw === 'CANCELADA') {
      throw new Error('Não é possível concluir uma Ordem de Montagem cancelada.');
    }

    const orderQuantity = Number(assemblyData.orderQuantity) || 1;

    let costPerSet = 0;
    let costPerUnit = 0;

    if (orderQuantity > 0 && totalComponentsCost > 0) {
      costPerSet = totalComponentsCost / orderQuantity;
    }

    if (totalComponentsQty > 0 && totalComponentsCost > 0) {
      costPerUnit = totalComponentsCost / totalComponentsQty;
    }

    transaction.update(assemblyRef, {
      status: 'CONCLUIDA',
      updatedAt: serverTimestamp(),
      updatedByUserId,
      concludedAt: serverTimestamp(),
      assemblyCostSnapshot: {
        totalComponentsQty,
        totalComponentsCost,
        costPerSet,
        costPerUnit,
      },
    });
  });

  return {
    assemblyId,
    totalComponentsQty,
    totalComponentsCost,
  };
};

// Export default padronizado
const service = {
  createNFEntryWithItemsAndLot,
  createAssemblyOrder,
  updateAssemblyOrderStatus,
  addAssemblyComponents,
  clearAssemblyComponents,
  finalizeAssemblyOrder,
};

export default service;